/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { useTranslations } from '@/hooks/useTranslations';
import { ListItemStandard } from '@ui5/webcomponents-react';
import React, { useState } from 'react';
// icons
import '@ui5/webcomponents-icons/dist/circle-task';
import '@ui5/webcomponents-icons/dist/initiative';
import '@ui5/webcomponents-icons/dist/status-completed';
// styles
import { useAppSelector } from '@/hooks';
import './DashboardSidebar.css';
import Sidebar from '../Sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectIsProductConfigurationChanged } from '@/store/Products';
import { IStep } from '@/types';
import { useGetCommodityCodeRunExistQuery, useGetUsersQuery } from '@/store/apis';
import { clsx } from '@/utils/utils';
import WarningMessageBox from '../WarningMessageBox';

type DashboardProps = {
    gridColumn?: string;
};

const DashboardSidebar: React.FC<DashboardProps> = ({ gridColumn }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const productList = useAppSelector(state => state.productList);
    const { aSubStepsForCurrentPhase, currentPhase, currentStep, currentRunId } = productList;

    const [navigatedStepId, setNavigatedStepId] = useState<string | null>(null);
    const isProductConfigurationChanged = useSelector(selectIsProductConfigurationChanged);
    const { data: users = [] } = useGetUsersQuery(currentRunId, {
        skip: !currentRunId,
    });

    const { data: isCommodityCodeExist } = useGetCommodityCodeRunExistQuery(currentRunId, {
        skip: !currentRunId,
    });

    const isUserStep = currentPhase.translationNameKey === 'USERS' && users.length;
    const isCommodityCodeStep = currentPhase.translationNameKey === 'CODES' && isCommodityCodeExist;

    const getStepIndex = (stepId: string): number => {
        return aSubStepsForCurrentPhase.findIndex((step: IStep) => step.id === stepId);
    };

    const getIcon = (currentSubStepId: string, indexSubStepId: string) => {
        const currentSubStepIndex = getStepIndex(currentSubStepId);
        if (aSubStepsForCurrentPhase[getStepIndex(indexSubStepId)].answered || isUserStep || isCommodityCodeStep) {
            return 'status-completed';
        }
        if (currentSubStepIndex == getStepIndex(indexSubStepId)) {
            return 'initiative';
        }
        return 'circle-task';
    };

    const handleStepClick = (stepId: string) => {
        if (stepId === currentStep.id) return;
        if (isProductConfigurationChanged) {
            setNavigatedStepId(stepId);
        } else {
            dispatch(actions.setCurrentStep(stepId));
        }
    };

    const handleConfirm = () => {
        if (navigatedStepId) {
            dispatch(actions.setCurrentStep(navigatedStepId));
            setNavigatedStepId(null);
        }
    };

    const handleCancel = () => {
        setNavigatedStepId(null);
    };

    return aSubStepsForCurrentPhase.length > 0 ? (
        <>
            <Sidebar
                headerText={t({ key: currentPhase.translationNameKey, defaultText: currentPhase.name })}
                items={aSubStepsForCurrentPhase.map(subStep => ({ ...subStep, label: t(subStep.translationTitleKey) }))}
                renderItem={item => (
                    <ListItemStandard
                        className={clsx({
                            'list-item-completed':
                                !!aSubStepsForCurrentPhase[getStepIndex(item.id)].answered || !!isUserStep || !!isCommodityCodeStep,
                            'list-item-selected': item.id === currentStep.id,
                        })}
                        key={item.id}
                        icon={getIcon(currentStep.id, item.id)}
                        onClick={() => handleStepClick(item.id)}
                    >
                        {t({ key: item.translationTitleKey, defaultText: item.name })}
                    </ListItemStandard>
                )}
                maxWidth={340}
                gridColumn={gridColumn}
            />
            {navigatedStepId && (
                <WarningMessageBox open dataTestId="continue-warning-step-change" onConfirm={handleConfirm} onCancel={handleCancel} />
            )}
        </>
    ) : null;
};

export default DashboardSidebar;
